/**
 * @file The Super Admin Merchant List's translation to and from core's `GET /core/v1/admin/merchants`.
 * FilterTable sends DRF parameters and the view reads Django's snake_case `simplified_list` row.
 */
import { ADMIN_MERCHANT_LIST_PARAMS } from '@/utils/endpoints.handwritten.js'

/** Table column key -> the field core sorts by. Core answers any other `sort` with a 400. */
const MERCHANT_LIST_SORT: Record<string, string> = {
  name: 'name',
  integration_type: 'integrationType'
}

/** What FilterTable passes its callback. */
export interface MerchantListTableParams {
  page: number
  page_size: number
  search?: string
  ordering?: string
}

/**
 * @param table - FilterTable's DRF-shaped params; left unchanged
 * @returns the query for `adminMerchants`: active merchants only, as Django served a super user
 */
export function buildMerchantListParams(
  table: MerchantListTableParams
): Record<string, string | number> {
  const ordering = table.ordering ?? ''
  const descending = ordering.startsWith('-')
  const field = MERCHANT_LIST_SORT[descending ? ordering.slice(1) : ordering]
  const params: Record<string, string | number> = {
    ...ADMIN_MERCHANT_LIST_PARAMS,
    page: table.page,
    limit: table.page_size,
    sort: field ? `${descending ? '-' : ''}${field}` : 'name'
  }
  const search = table.search?.trim()

  if (search) params.search = search

  return params
}

/** The empty page answered when no request is sent or the request fails. Fresh each call. */
export function emptyMerchantPage() {
  return { data: { count: 0, results: [] } }
}

// Intl rather than moment-timezone: the store imports this module, so the zone database would
// land in the entry chunk. America/Denver is the zone Django's US/Mountain names.
const MOUNTAIN_TIME = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Denver',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23'
})

/**
 * Django's format and zone for the Last Job column: `YYYY-MM-DD HH:mm:ss`, US/Mountain. An
 * unreadable time is '' rather than the RangeError that would fail the whole page for one cell.
 */
function toMountainTime(created: string): string {
  const date = new Date(created)

  if (Number.isNaN(date.getTime())) return ''

  const part = Object.fromEntries(
    MOUNTAIN_TIME.formatToParts(date).map(p => [p.type, p.value])
  )

  return `${part.year}-${part.month}-${part.day} ${part.hour}:${part.minute}:${part.second}`
}

/**
 * @param row - one core row: camelCase, with `lastJob` null for a merchant that never ran a job
 * @returns the snake_case `simplified_list` row the table, the row link and the job modal read
 */
export function toMerchantListRow(row: Record<string, any>) {
  const job = row.lastJob
  const time = job?.created ? toMountainTime(job.created) : ''

  return {
    id: row.id,
    pkid: row.pkid,
    name: row.name,
    support_email: row.supportEmail,
    integration_type: row.integrationType,
    last_job_time: time,
    last_job_label: time
      ? `Job ${job.jobType} executed at ${time} by ${job.createdByEmail ?? ''}`
      : ''
  }
}
